// src/pages/Login.tsx
import React, { useState } from 'react';
import { Card, Form, Input, Button, Typography, Alert } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useLogin } from '@refinedev/core';
import { Link } from 'react-router-dom';

const { Title } = Typography;

const Login: React.FC = () => {
  const { mutate: login, isLoading } = useLogin();
  const [error, setError] = useState<string | null>(null);

  const onFinish = async (values: any) => {
    setError(null);
    login(values, {
      onError: (error: any) => {
        console.log('Login error:', error);
        
        // Handle specific Firebase error codes
        const errorMessage = error?.message || error?.error?.message || '';
        
        if (errorMessage.includes('user-not-found')) {
          setError('No account found with this email address.');
        } else if (errorMessage.includes('wrong-password')) {
          setError('Incorrect password. Please try again.');
        } else if (errorMessage.includes('invalid-email')) {
          setError('Please enter a valid email address.');
        } else if (errorMessage.includes('user-disabled')) {
          setError('This account has been disabled. Please contact support.');
        } else if (errorMessage.includes('too-many-requests')) {
          setError('Too many failed attempts. Please try again later.');
        } else if (errorMessage.includes('network-request-failed')) {
          setError('Network error. Please check your connection.');
        } else {
          setError('Login failed. Please check your credentials and try again.');
        }
      },
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '20px'
    }}>
      <Card 
        style={{
          width: '100%',
          maxWidth: '400px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
        bodyStyle={{ padding: '40px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            Welcome Back
          </Title>
          <p style={{ color: '#666', margin: 0 }}>
            Login to Spectreviews
          </p>
        </div>

        <Form 
          name="login" 
          onFinish={onFinish} 
          layout="vertical"
          size="large"
          requiredMark={false}
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input 
              prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} 
              placeholder="Email address" 
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Please enter your password' }]}
          >
            <Input.Password 
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />} 
              placeholder="Password" 
              autoComplete="current-password"
            />
          </Form.Item>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
              style={{ marginBottom: '16px' }}
            />
          )}

          <Form.Item style={{ marginBottom: '16px' }}>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={isLoading} 
              block
              style={{ height: '40px' }}
            >
              Login
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            <Link 
              to="/forgot-password" 
              style={{ color: '#1890ff', fontSize: '14px' }}
            >
              Forgot password?
            </Link>
          </div>
        </Form>

        <div style={{ 
          textAlign: 'center', 
          marginTop: '24px',
          paddingTop: '24px',
          borderTop: '1px solid #f0f0f0'
        }}>
          <span style={{ color: '#666' }}>Don't have an account? </span>
          <Link to="/register" style={{ color: '#1890ff', fontWeight: 500 }}>
            Sign up
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default Login;